import { Capacitor, registerPlugin } from "@capacitor/core";

// Define the plugin interface
interface DatetimePickerPluginInterface {
  present(options: {
    mode: string;
    format?: string;
    value?: string;
    min?: string;
    max?: string;
    locale?: string;
    theme?: string;
    minuteInterval?: number;
  }): Promise<{ value: string }>;
  cancel(): Promise<void>;
}

// Register the plugin using Capacitor's registerPlugin
const DatetimePickerNative = registerPlugin<DatetimePickerPluginInterface>("DatetimePicker");

// Debug: Log when module loads
if (typeof window !== "undefined" && Capacitor.isNativePlatform()) {
  console.log("[DatetimePicker] Plugin registered, platform:", Capacitor.getPlatform());
}

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export function snapTo15Minutes(time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const snapped = Math.round(minutes / 15) * 15;
  const finalMinutes = snapped === 60 ? 0 : snapped;
  const finalHours = snapped === 60 ? (hours + 1) % 24 : hours;
  return `${String(finalHours).padStart(2, "0")}:${String(finalMinutes).padStart(2, "0")}`;
}

export interface DatePickerOptions {
  value?: Date;
  min?: Date;
  max?: Date;
}

export interface TimePickerOptions {
  value?: string; // HH:mm format
  is24h?: boolean;
}

// Format date as yyyy-MM-dd for the picker (local time, no timezone)
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Parse yyyy-MM-dd as local date
function parseDateLocal(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export async function presentDatePicker(options: DatePickerOptions = {}): Promise<Date | null> {
  console.log("[DatetimePicker] presentDatePicker called with:", options);

  if (!Capacitor.isNativePlatform()) {
    console.log("[DatetimePicker] Not on native platform");
    return null;
  }

  try {
    const presentOptions = {
      mode: "date",
      format: "yyyy-MM-dd",
      value: options.value ? formatDateLocal(options.value) : undefined,
      min: options.min ? formatDateLocal(options.min) : undefined,
      max: options.max ? formatDateLocal(options.max) : undefined,
      theme: "auto",
    };
    console.log("[DatetimePicker] Calling DatetimePickerNative.present for date with options:", JSON.stringify(presentOptions));

    const result = await DatetimePickerNative.present(presentOptions);
    console.log("[DatetimePicker] Result:", result);

    if (result.value) {
      return parseDateLocal(result.value);
    }
    return null;
  } catch (e) {
    console.log("[DatetimePicker] Error or cancelled:", e);
    return null;
  }
}

export async function presentTimePicker(options: TimePickerOptions = {}): Promise<string | null> {
  console.log("[DatetimePicker] presentTimePicker called with:", options);

  if (!Capacitor.isNativePlatform()) {
    console.log("[DatetimePicker] Not on native platform");
    return null;
  }

  try {
    // Use locale to control 12h vs 24h format display
    // en_US = 12-hour, en_GB = 24-hour
    const locale = options.is24h ? "en_GB" : "en_US";

    const presentOptions = {
      mode: "time",
      format: "HH:mm",
      value: options.value,
      locale,
      theme: "auto",
      minuteInterval: 15,
    };
    console.log("[DatetimePicker] Calling DatetimePickerNative.present for time with options:", JSON.stringify(presentOptions));

    const result = await DatetimePickerNative.present(presentOptions);
    console.log("[DatetimePicker] Result:", result);

    if (result.value) {
      // Result is already in HH:mm format
      return result.value;
    }
    return null;
  } catch (e) {
    console.log("[DatetimePicker] Error or cancelled:", e);
    return null;
  }
}
